import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Camera, Palette, Calculator, History } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

export default function LandingPage({ user }) {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);

  useEffect(() => {
    axios.get(`${API}/api/collections`).then(res => setCollections(res.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-stone-950 text-white" data-testid="landing-page" autoCorrect="off" spellCheck="false">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-stone-950/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-24 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-uol.png" alt="UOL Paper" className="h-20 w-auto" />
          </div>
          <Button
            data-testid="header-cta"
            onClick={() => navigate(user ? "/catalogo" : "/login")}
            className="rounded-none bg-white text-stone-950 hover:bg-stone-200 text-xs uppercase tracking-[0.2em] px-6"
          >
            {user ? "Vai al Catalogo" : "Accedi"}
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(180,160,120,0.08),transparent_70%)]" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <img src="/logo-uol.png" alt="UOL Paper" className="h-48 sm:h-64 w-auto mx-auto mb-8 animate-fade-in" />
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extralight leading-[1.1] mb-8 tracking-tight">
            Visualizza la tua
            <br />
            <span className="italic font-light text-amber-200/80">carta da parati</span>
            <br />
            nella tua stanza
          </h1>
          <p className="text-base sm:text-lg text-stone-400 max-w-2xl mx-auto mb-12 font-light leading-relaxed">
            Scatta una foto o carica un'immagine della stanza.
            Scegli dal catalogo UOL Paper.
            Guarda il risultato in tempo reale.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              data-testid="hero-start-btn"
              onClick={() => navigate(user ? "/catalogo" : "/login")}
              className="rounded-none bg-white text-stone-950 hover:bg-stone-200 text-sm uppercase tracking-[0.2em] px-10 py-6 h-auto"
            >
              Inizia Ora
              <ArrowRight className="w-4 h-4 ml-3" />
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="rounded-none border-white/20 text-white hover:bg-white/5 text-sm uppercase tracking-[0.2em] px-10 py-6 h-auto"
            >
              Come Funziona
            </Button>
          </div>
        </div>
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-px h-12 bg-gradient-to-b from-transparent to-white/30" />
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-32 px-6 bg-stone-900/50">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-stone-500 text-center mb-4">Come Funziona</p>
          <h2 className="text-2xl sm:text-3xl font-light text-center mb-20 tracking-tight">
            Tre semplici passaggi
          </h2>
          <div className="grid md:grid-cols-3 gap-16">
            {[
              { icon: Camera, step: "01", title: "Fotografa la stanza", desc: "Scatta una foto con la fotocamera o carica un'immagine dal tuo dispositivo" },
              { icon: Palette, step: "02", title: "Scegli la carta", desc: "Sfoglia il catalogo UOL Paper con tutte le linee Baseline e Artist Stripes" },
              { icon: Calculator, step: "03", title: "Simula e preventiva", desc: "Visualizza il risultato sulla parete e ottieni il preventivo immediato al mq" },
            ].map(({ icon: Icon, step, title, desc }) => (
              <div key={step} className="text-center group">
                <div className="w-16 h-16 mx-auto mb-6 border border-white/10 flex items-center justify-center group-hover:border-amber-300/40 transition-colors">
                  <Icon className="w-7 h-7 text-stone-400 group-hover:text-amber-300 transition-colors" />
                </div>
                <p className="text-xs text-stone-600 uppercase tracking-[0.3em] mb-3">{step}</p>
                <h3 className="text-lg font-light mb-3">{title}</h3>
                <p className="text-sm text-stone-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Collections */}
      <section className="py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-stone-500 text-center mb-4">Il Catalogo</p>
          <h2 className="text-2xl sm:text-3xl font-light text-center mb-6 tracking-tight">
            Collezioni UOL Paper
          </h2>
          <p className="text-base text-stone-500 text-center mb-16 max-w-xl mx-auto">
            Baselines e Artist Stripes. Design italiano di alta qualità per trasformare ogni ambiente.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {collections.map((col) => (
              <div
                key={col.collection_id}
                className="group relative aspect-square overflow-hidden cursor-pointer"
                onClick={() => navigate(user ? `/catalogo?col=${col.collection_id}` : "/login")}
              >
                {col.cover_image_url && (
                  <img src={col.cover_image_url} alt={col.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-stone-400 mb-1">
                    {col.collection_type === "artist" ? "Artist Stripes" : "Baseline"}
                  </p>
                  <p className="text-sm font-light text-white">{col.name}</p>
                  <p className="text-[10px] text-stone-400 mt-1">{col.wallpaper_count} soggetti</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-32 px-6 bg-stone-900/30 border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.4em] text-stone-500 text-center mb-4">Per Professionisti</p>
          <h2 className="text-2xl sm:text-3xl font-light text-center mb-16 tracking-tight">
            Pensato per architetti, rivenditori e GC
          </h2>
          <div className="grid sm:grid-cols-2 gap-8">
            {[
              { icon: Camera, title: "Foto o Upload", desc: "Scatta una foto con la fotocamera del dispositivo o carica un'immagine" },
              { icon: Palette, title: "Catalogo Completo", desc: "Tutte le linee UOL Paper sempre aggiornate con nuovi soggetti" },
              { icon: Calculator, title: "Preventivo Istantaneo", desc: "Calcolo automatico del costo al mq basato sulle dimensioni reali" },
              { icon: History, title: "Storico Simulazioni", desc: "Tutte le tue simulazioni salvate e consultabili in ogni momento" },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex gap-5 p-6 border border-white/5 hover:border-white/10 transition-colors">
                <Icon className="w-5 h-5 text-amber-300/60 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-base font-light mb-2">{title}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 text-center">
        <h2 className="text-3xl sm:text-4xl font-extralight mb-6 tracking-tight">
          Pronto a visualizzare?
        </h2>
        <p className="text-stone-500 mb-10 max-w-md mx-auto">
          Registrati gratuitamente e inizia a simulare le carte da parati UOL Paper nei tuoi ambienti.
        </p>
        <Button
          data-testid="cta-start-btn"
          onClick={() => navigate(user ? "/catalogo" : "/login")}
          className="rounded-none bg-white text-stone-950 hover:bg-stone-200 text-sm uppercase tracking-[0.2em] px-12 py-6 h-auto"
        >
          Registrati Gratis
          <ArrowRight className="w-4 h-4 ml-3" />
        </Button>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-stone-600">
          <img src="/logo-uol.png" alt="UOL Paper" className="h-8 w-auto opacity-50" />
          <span>Via A.Meucci, 8 — 37067 Valeggio sul Mincio (VR)</span>
          <a href="https://uolpaper.art" target="_blank" rel="noreferrer" className="hover:text-stone-400 transition-colors">
            uolpaper.art
          </a>
        </div>
      </footer>
    </div>
  );
}
